// ─── "From your coach": custom forms (check-ins and intakes) ───
// Home card listing the forms a coach assigned that are due now, the next one coming
// up, and the latest review note; plus the full-screen sheet that fills one in.
// API: my_forms / submit_form (ironlog-api/api/org/index.mjs). Pure rules live in
// utils/coachForms.js (tests/coach-forms.test.mjs).
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { V, Haptic } from '../utils/theme';
import { LS } from '../utils/storage';
import { Icons } from './Icons';
import { Card, Btn } from './ui';
import { FullSheet } from './FullSheet';
import { onAccent } from './kit';
import { today } from '../utils/helpers';
import { orgApi, orgErr, errorMessage } from '../utils/org';
import { useOrgFeed } from '../utils/orgFeed';
import {
  FORMS_CACHE_KEY, FORM_NOTE_SEEN_KEY, FORM_DRAFT_PREFIX, formDraftKey,
  formKindLabel, dueInfo, formsCardView, validateAnswers, answerErrors, coerceAnswers, answerErrorText, numberRange,
} from '../utils/coachForms';

const SR_ONLY = { position: 'absolute', width: 1, height: 1, padding: 0, margin: -1, overflow: 'hidden', clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap', border: 0 };

const eyebrow = () => ({ fontSize: 10, fontWeight: 700, color: V.accent, letterSpacing: '.08em', textTransform: 'uppercase', marginBottom: 2 });
const toneColor = (tone) => (tone === 'overdue' ? V.danger : tone === 'today' ? V.warn : V.text3);
const hasDraft = (id) => { const d = LS.get(formDraftKey(id)); return !!(d && d.answers && Object.keys(d.answers).length); };

export function CoachFormsCard() {
  const { enabled, data, load } = useOrgFeed('my_forms', { kind: 'forms', cacheKey: FORMS_CACHE_KEY });
  const [seenNote, setSeenNote] = useState(() => LS.get(FORM_NOTE_SEEN_KEY));
  const [open, setOpen] = useState(null);
  const td = today();
  const view = formsCardView(data, td, seenNote);

  // Drafts of forms that are no longer assigned (answered elsewhere, unassigned) are dropped.
  useEffect(() => {
    if (!data) return;
    const live = new Set([...(data.due || []), ...(data.upcoming || [])].map(a => a && a.assignment_id));
    try {
      const stale = [];
      for (let i = 0; i < localStorage.length; i++) {
        const k = localStorage.key(i);
        if (k && k.startsWith(FORM_DRAFT_PREFIX) && !live.has(k.slice(FORM_DRAFT_PREFIX.length))) stale.push(k);
      }
      stale.forEach(k => LS.del(k));
    } catch {}
  }, [data]);

  const close = useCallback(() => setOpen(null), []);
  if (!enabled) return null;
  // The sheet outlives the card: answering the last form hides the card while the sheet
  // still shows its "sent" state.
  const sheet = open ? <FormFillSheet item={open} onClose={close} onSubmitted={load} /> : null;
  if (!view.visible) return sheet;
  const dismissNote = () => { LS.set(FORM_NOTE_SEEN_KEY, view.note.id); setSeenNote(view.note.id); };

  return (
    <div data-testid="coach-forms-card">
      <Card style={{ padding: 14, border: `1px solid ${V.accent}20`, background: `${V.accent}05` }}>
        <div style={eyebrow()}>From your coach</div>
        {view.due.length > 0 && (
          <>
            <div style={{ fontSize: 13, fontWeight: 700, color: V.text, marginBottom: 8 }}>
              {view.due.length === 1 ? '1 form to fill in' : `${view.due.length} forms to fill in`}
            </div>
            <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
              {view.due.map(item => {
                const due = dueInfo(item.next_due, td);
                const draft = hasDraft(item.assignment_id);
                return (
                  <li key={item.assignment_id} data-testid="coach-form-row" style={{ display: 'flex', alignItems: 'center', gap: 10, paddingTop: 8, paddingBottom: 8, paddingLeft: 10, paddingRight: 8,
                    borderRadius: 10, background: V.inputBg, border: `1px solid ${V.cardBorder}` }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: V.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.form.title}</div>
                      <div style={{ fontSize: 11, color: V.text3, marginTop: 2 }}>
                        {formKindLabel(item)} · <span style={{ color: toneColor(due.tone), fontWeight: due.tone === 'upcoming' ? 400 : 600 }}>{due.text}</span>
                        {draft ? ' · Draft saved' : ''}
                      </div>
                    </div>
                    <Btn v="small" onClick={() => setOpen(item)} s={{ flexShrink: 0, minHeight: 36 }}>
                      <span>{draft ? 'Continue' : 'Open'}</span><span style={SR_ONLY}> {item.form.title}</span>
                    </Btn>
                  </li>
                );
              })}
            </ul>
          </>
        )}
        {view.next && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: V.text3 }} data-testid="coach-form-next">
            {Icons.calendar({ size: 13, color: V.text3 })}
            <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              Next: <span style={{ color: V.text, fontWeight: 600 }}>{view.next.form.title}</span> · {dueInfo(view.next.next_due, td).text}
            </span>
          </div>
        )}
        {view.note && (
          <div data-testid="coach-form-note" style={{ marginTop: view.due.length || view.next ? 10 : 2, paddingTop: 10, paddingBottom: 10, paddingLeft: 12, paddingRight: 12,
            borderRadius: 10, background: V.inputBg, borderLeft: `3px solid ${V.accent}` }}>
            <div style={{ fontSize: 11, color: V.text3, marginBottom: 4 }}>Coach note on «{view.note.title || 'your form'}»</div>
            <div style={{ fontSize: 13, color: V.text, lineHeight: 1.45, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{view.note.review_note}</div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 6 }}>
              <Btn v="ghost" onClick={dismissNote} s={{ minHeight: 32, padding: '4px 10px', fontSize: 12, color: V.accent }}>Got it</Btn>
            </div>
          </div>
        )}
      </Card>
      {sheet}
    </div>
  );
}

// ─── Fill-in sheet ───

const optionStyle = (on) => ({
  display: 'flex', alignItems: 'center', gap: 10, width: '100%', textAlign: 'left', minHeight: 44,
  paddingTop: 10, paddingBottom: 10, paddingLeft: 12, paddingRight: 12, borderRadius: 12, cursor: 'pointer', fontFamily: V.font, fontSize: 14,
  background: on ? `${V.accent}14` : V.inputBg, border: `1px solid ${on ? V.accent : V.cardBorder}`, color: V.text,
  WebkitTapHighlightColor: 'transparent',
});
const inputStyle = (err) => ({
  width: '100%', boxSizing: 'border-box', paddingTop: 12, paddingBottom: 12, paddingLeft: 14, paddingRight: 14, minHeight: 44,
  background: V.inputBg, border: `1px solid ${err ? V.danger : V.cardBorder}`, borderRadius: 12, color: V.text,
  fontSize: 16, fontFamily: V.font, outline: 'none', WebkitAppearance: 'none',
});

function Mark({ on, square }) {
  return (
    <span aria-hidden="true" style={{ width: 20, height: 20, flexShrink: 0, borderRadius: square ? 6 : 10, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      border: `2px solid ${on ? V.accent : V.text3}`, background: on && square ? V.accent : 'transparent' }}>
      {on && (square ? Icons.check({ size: 12, color: onAccent(), strokeWidth: 3 }) : <span style={{ width: 10, height: 10, borderRadius: 5, background: V.accent }} />)}
    </span>
  );
}

function QuestionInput({ q, value, onChange, ids, invalid }) {
  const aria = { 'aria-describedby': ids.describedBy, 'aria-invalid': invalid || undefined };
  switch (q.type) {
    case 'text':
      return <input id={ids.input} type="text" value={typeof value === 'string' ? value : ''} maxLength={200} autoComplete="off"
        aria-required={q.required || undefined} {...aria} onChange={e => onChange(e.target.value)} style={inputStyle(invalid)} />;
    case 'long_text': {
      const v = typeof value === 'string' ? value : '';
      return (
        <>
          <textarea id={ids.input} value={v} maxLength={4000} rows={4} aria-required={q.required || undefined} {...aria}
            onChange={e => onChange(e.target.value)} style={{ ...inputStyle(invalid), resize: 'vertical', lineHeight: 1.45, minHeight: 96 }} />
          {v.length > 3500 && <div style={{ fontSize: 11, color: V.text3, textAlign: 'right', marginTop: 4 }}>{v.length} / 4000</div>}
        </>
      );
    }
    case 'number': {
      const v = value === undefined || value === null ? '' : String(value);
      return (
        <div style={{ position: 'relative' }}>
          <input id={ids.input} type="text" inputMode="decimal" autoComplete="off" value={v} aria-required={q.required || undefined} {...aria}
            onChange={e => onChange(e.target.value)}
            style={{ ...inputStyle(invalid), fontFamily: V.mono, paddingRight: q.unit ? 56 : 14 }} />
          {q.unit && <span aria-hidden="true" style={{ position: 'absolute', right: 14, top: '50%', transform: 'translateY(-50%)', fontSize: 13, color: V.text3, pointerEvents: 'none' }}>{q.unit}</span>}
        </div>
      );
    }
    case 'scale': {
      const min = q.min ?? 1, max = q.max ?? 10;
      const nums = Array.from({ length: max - min + 1 }, (_, i) => min + i);
      return (
        <div role="radiogroup" aria-labelledby={ids.label} aria-required={q.required || undefined} {...aria} style={{ display: 'flex', gap: 4 }}>
          {nums.map(n => {
            const on = value === n;
            return (
              <button key={n} type="button" role="radio" aria-checked={on} onClick={() => onChange(on && !q.required ? undefined : n)}
                style={{ flex: '1 1 0', minWidth: 0, height: 44, borderRadius: 10, cursor: 'pointer', fontFamily: V.mono, fontSize: 14, fontWeight: 700,
                  background: on ? V.accent : V.inputBg, color: on ? onAccent() : V.text, border: `1px solid ${on ? V.accent : V.cardBorder}`,
                  paddingLeft: 0, paddingRight: 0, WebkitTapHighlightColor: 'transparent' }}>{n}</button>
            );
          })}
        </div>
      );
    }
    case 'yes_no':
      return (
        <div role="radiogroup" aria-labelledby={ids.label} aria-required={q.required || undefined} {...aria} style={{ display: 'flex', gap: 8 }}>
          {[[true, 'Yes'], [false, 'No']].map(([b, l]) => {
            const on = value === b;
            return (
              <button key={l} type="button" role="radio" aria-checked={on} onClick={() => onChange(on && !q.required ? undefined : b)}
                style={{ ...optionStyle(on), flex: 1, justifyContent: 'center', fontWeight: 600 }}>{l}</button>
            );
          })}
        </div>
      );
    case 'choice':
      return (
        <div role="radiogroup" aria-labelledby={ids.label} aria-required={q.required || undefined} {...aria} style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {(q.options || []).map(o => {
            const on = value === o;
            return (
              <button key={o} type="button" role="radio" aria-checked={on} onClick={() => onChange(on && !q.required ? undefined : o)} style={optionStyle(on)}>
                <Mark on={on} /><span style={{ minWidth: 0, overflowWrap: 'anywhere' }}>{o}</span>
              </button>
            );
          })}
        </div>
      );
    case 'multi_choice': {
      const sel = Array.isArray(value) ? value : [];
      return (
        <div role="group" aria-labelledby={ids.label} {...aria} style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {(q.options || []).map(o => {
            const on = sel.includes(o);
            return (
              <button key={o} type="button" role="checkbox" aria-checked={on} style={optionStyle(on)}
                onClick={() => onChange(on ? sel.filter(x => x !== o) : [...sel, o])}>
                <Mark on={on} square /><span style={{ minWidth: 0, overflowWrap: 'anywhere' }}>{o}</span>
              </button>
            );
          })}
        </div>
      );
    }
    default:
      return <div style={{ fontSize: 12, color: V.text3 }}>Update the app to answer this question.</div>;
  }
}

function Question({ q, index, value, error, onChange, setRef }) {
  const base = `cf-q${index}`;
  const ids = { label: `${base}-label`, input: `${base}-input`, help: `${base}-help`, range: `${base}-range`, err: `${base}-err` };
  const range = q.type === 'number' ? numberRange(q) : '';
  ids.describedBy = [q.help ? ids.help : null, range && !error ? ids.range : null, error ? ids.err : null].filter(Boolean).join(' ') || undefined;
  const labelled = q.type === 'text' || q.type === 'long_text' || q.type === 'number';
  const LabelTag = labelled ? 'label' : 'div';
  return (
    <div ref={setRef} data-testid={`form-q-${q.id}`} style={{ paddingTop: 14, paddingBottom: 14, borderTop: index ? `1px solid ${V.divider}` : 'none' }}>
      <LabelTag id={ids.label} {...(labelled ? { htmlFor: ids.input } : {})} style={{ display: 'block', fontSize: 14, fontWeight: 600, color: V.text, lineHeight: 1.4, marginBottom: q.help ? 2 : 8 }}>
        {q.label}
        {q.required && <><span aria-hidden="true" style={{ color: V.accent }}> *</span>{!labelled && q.type === 'multi_choice' && <span style={SR_ONLY}> (required)</span>}</>}
      </LabelTag>
      {q.help && <div id={ids.help} style={{ fontSize: 12, color: V.text3, lineHeight: 1.4, marginBottom: 8 }}>{q.help}</div>}
      <QuestionInput q={q} value={value} onChange={onChange} ids={ids} invalid={!!error} />
      {range && !error && <div id={ids.range} style={{ fontSize: 11, color: V.text3, marginTop: 4 }}>{range}</div>}
      {error && <div id={ids.err} role="alert" style={{ fontSize: 12, color: V.danger, marginTop: 6, fontWeight: 600 }}>{answerErrorText(q, error)}</div>}
    </div>
  );
}

// Full-screen form. The answers are kept as a draft per assignment (localStorage) so closing
// half-way loses nothing; the draft is cleared once the form is sent.
export function FormFillSheet({ item, onClose, onSubmitted }) {
  const questions = Array.isArray(item.form.questions) ? item.form.questions : [];
  const key = formDraftKey(item.assignment_id);
  const [draft, setDraft] = useState(() => { const d = LS.get(key); return d && d.answers && typeof d.answers === 'object' ? d.answers : {}; });
  const [errors, setErrors] = useState({});
  const [topErr, setTopErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [gone, setGone] = useState(false); // unassigned or archived while open: nothing to send to
  const qRefs = useRef({});
  const headRef = useRef(null);
  const doneRef = useRef(null);
  const sentRef = useRef(false);
  const td = today();
  const due = dueInfo(item.next_due, td);

  // Focus the heading on open, give focus back to the opener on close, Escape closes.
  useEffect(() => {
    const prev = document.activeElement;
    headRef.current?.focus();
    return () => { try { if (prev && prev.isConnected) prev.focus(); } catch {} };
  }, []);
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);
  useEffect(() => {
    if (sentRef.current) return;
    if (Object.keys(draft).length) LS.set(key, { answers: draft, due: item.next_due, saved_at: Date.now() });
    else LS.del(key);
  }, [draft, key, item.next_due]);
  useEffect(() => {
    if (!sent) return undefined;
    doneRef.current?.focus();
    const t = setTimeout(onClose, 1600);
    return () => clearTimeout(t);
  }, [sent, onClose]);

  const setAnswer = (id, v) => {
    setDraft(d => { const n = { ...d }; if (v === undefined) delete n[id]; else n[id] = v; return n; });
    setErrors(e => { if (!e[id]) return e; const n = { ...e }; delete n[id]; return n; });
  };
  const focusQuestion = (id) => {
    const el = qRefs.current[id];
    if (!el) return;
    try { el.scrollIntoView({ block: 'center', behavior: 'smooth' }); } catch {}
    const target = el.querySelector('input, textarea, button');
    if (target) setTimeout(() => target.focus({ preventScroll: true }), 50);
  };

  const submit = async () => {
    if (busy) return;
    const answers = coerceAnswers(questions, draft);
    const errs = answerErrors(questions, answers);
    const bad = questions.filter(q => errs[q.id]);
    if (bad.length) {
      setErrors(errs);
      setTopErr(null);
      focusQuestion(bad[0].id);
      return;
    }
    setBusy(true); setTopErr(null); setErrors({});
    try {
      await orgApi('submit_form', { assignment_id: item.assignment_id, answers: validateAnswers(questions, answers).answers, today: td });
      sentRef.current = true;
      LS.del(key);
      Haptic.success();
      setSent(true);
      if (onSubmitted) onSubmitted();
    } catch (e) {
      const er = orgErr(e);
      const q = er.question && questions.find(x => x.id === er.question);
      if ((er.code === 'answer_required' || er.code === 'invalid_answer') && q) {
        setErrors({ [q.id]: er.code });
        focusQuestion(q.id);
      } else if (er.code === 'form_archived' || er.code === 'not_found' || er.status === 404) {
        LS.del(key); sentRef.current = true; setGone(true);
        setTopErr('Your coach has removed this form, so it can no longer be sent.');
        if (onSubmitted) onSubmitted();
      } else {
        setTopErr(errorMessage(er));
      }
      setBusy(false);
    }
  };

  const requiredCount = questions.filter(q => q.required).length;
  // Validation count follows the answers: it shrinks as each flagged question is fixed.
  const errCount = Object.keys(errors).length;
  const footerMsg = topErr || (errCount ? (errCount === 1 ? '1 question needs your attention.' : `${errCount} questions need your attention.`) : null);
  const footer = sent ? null : (
    <div>
      {footerMsg && <div role="alert" data-testid="form-error" style={{ fontSize: 13, color: V.danger, fontWeight: 600, marginBottom: 10, textAlign: 'center' }}>{footerMsg}</div>}
      <Btn full onClick={submit} disabled={busy || gone}>{busy ? 'Sending…' : 'Send to coach'}</Btn>
      <div style={{ fontSize: 11, color: V.text3, textAlign: 'center', marginTop: 8 }}>Your answers stay on this device until you send them.</div>
    </div>
  );

  return (
    <FullSheet title={formKindLabel(item)} ariaLabel={item.form.title} onClose={onClose} closeLabel="Close" footer={footer} zIndex={9991}>
      {sent ? (
        <div role="status" data-testid="form-sent" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', paddingTop: 48, gap: 12 }}>
          <div aria-hidden="true" style={{ width: 64, height: 64, borderRadius: 32, background: `linear-gradient(135deg,${V.accent},${V.accent2})`, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: V.glow }}>
            {Icons.check({ size: 30, color: onAccent(), strokeWidth: 3 })}
          </div>
          <div ref={doneRef} tabIndex={-1} style={{ fontSize: 18, fontWeight: 700, color: V.text, outline: 'none' }}>Sent to your coach</div>
          <div style={{ fontSize: 13, color: V.text3 }}>{item.cadence === 'weekly' ? "You'll see it again next week." : 'Thanks for filling it in.'}</div>
          <Btn v="secondary" onClick={onClose} s={{ marginTop: 8 }}>Done</Btn>
        </div>
      ) : (
        <div>
          <h2 ref={headRef} tabIndex={-1} style={{ fontSize: 20, fontWeight: 800, color: V.text, margin: 0, outline: 'none', lineHeight: 1.25 }}>{item.form.title}</h2>
          <div style={{ fontSize: 12, color: toneColor(due.tone), marginTop: 4, fontWeight: 600 }}>{due.text}</div>
          {item.form.description && <p style={{ fontSize: 13, color: V.text2, lineHeight: 1.5, marginTop: 10, marginBottom: 0, whiteSpace: 'pre-wrap' }}>{item.form.description}</p>}
          {requiredCount > 0 && <div style={{ fontSize: 11, color: V.text3, marginTop: 10 }}><span aria-hidden="true" style={{ color: V.accent }}>*</span> Required</div>}
          <div style={{ marginTop: 6 }}>
            {questions.map((q, i) => (
              <Question key={q.id} q={q} index={i} value={draft[q.id]} error={errors[q.id]} onChange={(v) => setAnswer(q.id, v)}
                setRef={(el) => { qRefs.current[q.id] = el; }} />
            ))}
          </div>
        </div>
      )}
    </FullSheet>
  );
}
